import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo } from 'react'
import { Formik } from 'formik'
import { makeStyles, shorthands, tokens } from '@fluentui/react-components'
import { LegalAidCertificateProps } from '../../../pages/File/Registration/LegalAidCertificateOverview'
import Loading from '../../../components/Loading'
import { formatIsoDate } from '../../../helpers/dateHelper'
import { Alert } from '@fluentui/react-components/unstable'
import { IFileDto } from '../../../config/fileConfig'
import useAxios from '../../../hooks/useAxios'
import { getId } from '@fluentui/react'
import FormContainer from './FormContainer'
import { CreateDto, FilePartyAction, FilePartyRole, MainDto } from '../../../config/filePartyConfig'
import { AxiosResponse } from 'axios'
import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import { useDispatch } from 'react-redux'

import * as Yup from 'yup'
import { Currency } from '../../../config/i18nConfig'

type BaseLegalAidCertificate = {
    applied: string
    issued: string
    reference: string
    baseHours: string
    travelHours: string
    travelDistance: string
}

export type LegalAidCertificate = BaseLegalAidCertificate & {
    id: string
    file: {
        id: string
    }
    baseFee: string
    travelFee: string
    travelExpenses: string
    fixedCompensation: string
    contribution: string
    user: {
        id: string
    }
    contributionInvoice: {
        id: string
        number: string
        pdfId: string
    }
    settlementInvoice: {
        id: string
        number: string
        pdfId: string
    }
    clientPivot: {
        id: string
    }
    legalAidBoardPivot: {
        id: string
    }
    currency: Currency
}

export type LegalAidCertificateDto = BaseLegalAidCertificate & {
    fileId: string
    userId: string
    contributionInvoiceId: string
    settlementInvoiceId: string
    clientPivotId: string
    legalAidBoardPivotId: string
    baseFee: string
    travelFee: string
    travelExpenses: string
    contribution: string
    fixedCompensation: string
    currency: string
}

const useFormStyles = makeStyles({
    alert: {
        ...shorthands.margin(0, tokens.spacingHorizontalS),
    },
})

export type LegalAidCertificateSettings = {
    baseFeeHour: string
    travelFeeHour: string
    travelKm: string
    fixedCompensation: string
    currency: string
    vatType: string
    contributionInvoice: {
        templateId: string // The invoice template.
        feeTemplateId: string // The group template to add the own contribution fee to.
    }
    settlementInvoice: {
        templateId: string // The invoice template.
        feeTemplateId: string // The group template to add the base fee to.
        travelTemplateId: string // The group template to add the travel distance compensation to.
        contributionTemplateId: string // The group template to add the own contribution deposit invoice to.
    }
    legalAidBoardPartyId: string
    legalAidBoardRole: string
    clientRole: string
}

const getPivotIdByRole = (parties: MainDto[], role: string): string | undefined => {
    const matches = parties.filter(party => party.role === role)
    if (matches.length === 1) {
        return matches[0].pivotId
    }
    return undefined
}

const mapCertificate = (
    certificate: LegalAidCertificate,
    settings: LegalAidCertificateSettings,
    file: IFileDto,
    parties: MainDto[],
): LegalAidCertificateDto => ({
    // Master data
    clientPivotId: certificate?.clientPivot?.id || getPivotIdByRole(parties, settings.clientRole || FilePartyRole.Client),
    legalAidBoardPivotId: certificate?.legalAidBoardPivot?.id,
    applied: certificate?.applied || formatIsoDate(new Date()),
    issued: certificate?.issued,
    reference: certificate?.reference,

    // Compensation calculation
    baseHours: certificate?.baseHours || '0.00',
    baseFee: certificate?.baseFee,
    travelHours: certificate?.travelHours || '0.00',
    travelFee: certificate?.travelFee,
    travelDistance: certificate?.travelDistance || '0',
    travelExpenses: certificate?.travelExpenses,
    contribution: certificate?.contribution,
    fixedCompensation: certificate?.fixedCompensation || settings.fixedCompensation,

    // Invoices
    contributionInvoiceId: certificate?.contributionInvoice?.id,
    settlementInvoiceId: certificate?.settlementInvoice?.id,

    // Hidden fields
    fileId: certificate?.file?.id || file?.id,
    userId: certificate?.user?.id || file?.owner.id,
    currency: certificate?.currency?.alpha || settings.currency || 'EUR',
})

const requiresIssued =
    (message?: string) =>
    ([issued], schema) =>
        issued?.length > 0 ? schema.required(message) : schema.nullable()

export const LegalAidCertificateValidationSchema = Yup.object().shape({
    clientPivotId: Yup.string().uuid().required(),
    legalAidBoardPivotId: Yup.string().uuid(), // Validation for legal aid board ID is a custom procedure in the submit method.
    applied: Yup.string().required(),
    issued: Yup.string().nullable(),
    reference: Yup.string().nullable().when('issued', requiresIssued('Kenmerk is verplicht bij een toekenning')),

    // Compensation calculation
    baseHours: Yup.number().min(0).when('issued', requiresIssued()),
    baseFee: Yup.number().min(0).when('issued', requiresIssued()),
    travelHours: Yup.number().min(0).when('issued', requiresIssued()),
    travelFee: Yup.number().min(0).when('issued', requiresIssued()),
    travelDistance: Yup.number().min(0).when('issued', requiresIssued()),
    travelExpenses: Yup.number().min(0).when('issued', requiresIssued()),
    contribution: Yup.number().min(0).when('issued', requiresIssued()),
    fixedCompensation: Yup.number().min(0).when('issued', requiresIssued()),

    // Invoices
    contributionInvoiceId: Yup.string().uuid(),
    settlementInvoiceId: Yup.string().uuid(),

    // Hidden fields
    fileId: Yup.string().uuid().required(),
    userId: Yup.string().uuid().required(),
    currency: Yup.string().length(3).required(), // Alpha-3 currency code.
})

export function LegalAidCertificateForm(props: LegalAidCertificateProps): ReactElement {
    const axios = useAxios()
    const dispatch = useDispatch()
    const styles = useFormStyles()
    const { settings } = useContext<GlobalContextType>(GlobalContext)
    const key = useMemo(() => getId(props.selected?.id), [props.certificates.data, props.selected?.id])
    const initialValues = useMemo<LegalAidCertificateDto>(
        () =>
            props.file.loaded && props.parties.loaded && props.selected
                ? mapCertificate(
                      props.certificates.data?.find(certificate => certificate.id === props.selected.id),
                      settings.legalAidCertificate,
                      props.file.data,
                      props.parties.data,
                  )
                : undefined,
        [props.file.loaded, props.parties.loaded, props.certificates.data, props.selected?.id, settings.legalAidCertificate],
    )

    const defaultPartyId = useMemo(() => {
        if (props.legalAidBoards.loaded && props.parties.loaded && settings?.legalAidCertificate) {
            // Take the first one from the list if there is only a single legal aid board party added to this file.
            const legalAidBoardPartyIds = props.legalAidBoards.data.map(party => party.id)
            const matches = props.parties.data.filter(party => legalAidBoardPartyIds.includes(party.id))

            if (matches.length === 1) {
                return matches[0].id
            }

            // Take the party ID from the settings if there is one set.
            if (matches.length === 0 && settings.legalAidCertificate.legalAidBoardPartyId) {
                return settings.legalAidCertificate.legalAidBoardPartyId
            }
        }
        return undefined
    }, [props.legalAidBoards.data, props.parties.data, settings])

    useEffect(() => {
        if (initialValues) {
            props.setSelected({
                ...props.selected,
                legalAidBoardPartyId: props.parties.data.find(party => party.pivotId === initialValues.legalAidBoardPivotId)?.id || defaultPartyId,
            })
        }
    }, [initialValues, defaultPartyId])

    if (!props.file.loaded || !props.parties.loaded || !settings) {
        return <Loading />
    }

    if (!initialValues) {
        return (
            <div className={styles.alert}>
                <Alert intent="info">Er is geen toevoeging geselecteerd. Kies een bestaande toevoeging uit de lijst of maak een nieuwe aan.</Alert>
            </div>
        )
    }

    return (
        <Formik<LegalAidCertificateDto>
            // Force reload the Formik component when we have selected another certificate, because otherwise the
            // pinned fields won't update correctly.
            key={key}
            validationSchema={LegalAidCertificateValidationSchema}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            initialValues={initialValues}
            onSubmit={async (dto: LegalAidCertificateDto, { setSubmitting, setFieldError }) => {
                // Create the legal aid board relation first.
                if (!dto.legalAidBoardPivotId && props.selected.legalAidBoardPartyId) {
                    dto.legalAidBoardPivotId = await axios
                        .post<string>('/file-party', {
                            fileId: dto.fileId,
                            partyId: props.selected.legalAidBoardPartyId,
                            role: settings.legalAidCertificate.legalAidBoardRole || FilePartyRole.Debtor,
                        } as CreateDto)
                        .then(response => {
                            dispatch({ type: FilePartyAction.ResetOverview })
                            return response.data
                        })
                }
                if (!dto.legalAidBoardPivotId) {
                    setFieldError('legalAidBoardPivotId', 'Arrondissement is een verplicht veld')
                } else {
                    const response: AxiosResponse<string> = props.selected.isNew
                        ? await axios.post('/registration/legal-aid-certificate', dto)
                        : await axios.put(`/registration/legal-aid-certificate/${props.selected.id}`, dto)

                    // Set the stored legal aid certificate as the current one.
                    props.setSelected({
                        ...props.selected,
                        id: response.data,
                        isNew: false,
                        isDirty: false,
                    })
                    props.reset()
                }

                setSubmitting(false)
            }}
        >
            <FormContainer {...props} />
        </Formik>
    )
}
